import React, { useState, useEffect } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { toast } from 'sonner';
import { getArchetype } from './associations/archetypeMap';

// 13 unique archetype templates
import ArchiveTemplate from './associations/ArchiveTemplate';
import AmphitheaterTemplate from './associations/AmphitheaterTemplate';
import HearthTemplate from './associations/HearthTemplate';
import ObservatoryTemplate from './associations/ObservatoryTemplate';
import RebelTemplate from './associations/RebelTemplate';
import KineticTemplate from './associations/KineticTemplate';
import FolkloreTemplate from './associations/FolkloreTemplate';
import SovereignTemplate from './associations/SovereignTemplate';
import BazaarTemplate from './associations/BazaarTemplate';
import SanctuaryTemplate from './associations/SanctuaryTemplate';
import SalonTemplate from './associations/SalonTemplate';
import StageTemplate from './associations/StageTemplate';
import LaboratoryTemplate from './associations/LaboratoryTemplate';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_BASE = process.env.REACT_APP_BACKEND_URL;

const TEMPLATES = {
  archive: ArchiveTemplate,
  amphitheater: AmphitheaterTemplate,
  hearth: HearthTemplate,
  observatory: ObservatoryTemplate,
  rebel: RebelTemplate,
  kinetic: KineticTemplate,
  folklore: FolkloreTemplate,
  sovereign: SovereignTemplate,
  bazaar: BazaarTemplate,
  sanctuary: SanctuaryTemplate,
  salon: SalonTemplate,
  stage: StageTemplate,
  laboratory: LaboratoryTemplate,
};

const AssociationDetailPage = () => {
  const { slug: paramSlug } = useParams();
  const location = useLocation();
  const [association, setAssociation] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  const slug = paramSlug || location.pathname.replace('/', '').toLowerCase();

  const handleShare = async () => {
    const url = `${SITE_BASE}/${association?.slug || slug}`;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied!');
        return;
      }
    } catch (_) { /* fallback */ }
    const ta = document.createElement('textarea');
    ta.value = url; ta.style.position = 'fixed'; ta.style.left = '-999999px';
    document.body.appendChild(ta); ta.focus(); ta.select();
    try { document.execCommand('copy'); toast.success('Link copied!'); } catch (_) { toast.error('Could not copy link.'); }
    document.body.removeChild(ta);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        let assocRes;
        try { assocRes = await axios.get(`${API_URL}/associations/by-slug/${slug}`); }
        catch (_) { assocRes = await axios.get(`${API_URL}/associations/${slug}`); }
        setAssociation(assocRes.data);
        try {
          const activitiesRes = await axios.get(`${API_URL}/activities?association_id=${assocRes.data.id}&status=approved`);
          setActivities(activitiesRes.data);
        } catch (_) { /* no activities */ }
      } catch (_) { /* not found */ } finally { setLoading(false); }
    };
    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white/30" />
      </div>
    );
  }

  if (!association) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center gap-4">
        <h1 className="font-serif text-2xl font-bold text-white">Association Not Found</h1>
        <Link to="/associations" className="text-sm text-white/60 hover:text-white underline">Back to Associations</Link>
      </div>
    );
  }

  const theme = association.theme || { primary: '#005C46', secondary: '#FFD700', accent: '#B91C1C', bg: '#0A1A14' };
  const archetype = getArchetype(association.slug || slug, association.archetype);
  const Template = TEMPLATES[archetype] || HearthTemplate;
  const shareUrl = `${SITE_BASE}/${association.slug || slug}`;

  return (
    <>
      <Helmet>
        <title>{association.short_name} - {association.name || ''}</title>
        <meta name="description" content={`${association.description}. ${association.cultural_highlights || ''}`} />
        <meta property="og:title" content={`${association.short_name} | CCA Montreal`} />
        <meta property="og:description" content={association.description} />
        <meta property="og:image" content={association.logo_url || association.image_url} />
        <meta property="og:url" content={shareUrl} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <Template
        association={association}
        activities={activities}
        theme={theme}
        onShare={handleShare}
      />
    </>
  );
};

export default AssociationDetailPage;
